import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, ArrowLeft, MailCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';

export default function LoginPage() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get('redirect') || '';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [twoFa, setTwoFa] = useState(false);        // 2fa gate active
  const [totpCode, setTotpCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  // Forgot password
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (user) {
      if (redirect) return nav(redirect);
      nav(user.role === 'admin' ? '/admin' : user.role === 'commercial' ? '/commercial-portal' : '/portal');
    }
  }, [user, nav, redirect]);

  const doLogin = async (extra = {}) => {
    setLoading(true);
    try {
      const u = await login(email, password, extra);
      toast.success(`Welcome back, ${u.name.split(' ')[0]}!`);
      if (redirect) nav(redirect);
      else nav(u.role === 'admin' ? '/admin' : u.role === 'commercial' ? '/commercial-portal' : '/portal');
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend signals 2FA required by returning {code: 'totp_required'}
      if (detail && typeof detail === 'object' && detail.code === 'totp_required') {
        setTwoFa(true);
      } else {
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Login failed');
        toast.error(msg);
      }
    } finally { setLoading(false); }
  };

  const submitCreds = (e) => { e.preventDefault(); doLogin(); };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error('Enter your email'); return; }
    setForgotBusy(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail.trim() });
      setForgotSent(true);
    } catch {
      toast.error('Could not send reset email — try again shortly');
    } finally { setForgotBusy(false); }
  };

  const submitTotp = (e) => {
    e.preventDefault();
    if (useBackup) {
      if (!backupCode.trim()) { toast.error('Enter a backup code'); return; }
      doLogin({ backup_code: backupCode.trim() });
    } else {
      if (!/^\d{6}$/.test(totpCode.replace(/\s/g, ''))) { toast.error('Enter the 6-digit code from your authenticator'); return; }
      doLogin({ totp_code: totpCode.replace(/\s/g, '') });
    }
  };

  return (
    <Layout hideFooter>
      <section className="max-w-md mx-auto px-4 sm:px-6 py-14">
        <div className="flex flex-col items-center gap-3 mb-6">
          <img src="/logo.png" alt="Royal Palm Services" className="h-24 w-auto object-contain" />
          <div className="font-headline text-2xl text-[#17352D]">{twoFa ? 'Two-factor code' : 'Sign in'}</div>
        </div>

        {!twoFa ? (
          <form onSubmit={submitCreds} className="rounded-2xl border border-[#E6E9EE] bg-white p-6 space-y-4" data-testid="login-form">
            <div><Label htmlFor="e">Email</Label><Input id="e" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="login-email-input" /></div>
            <div><Label htmlFor="p">Password</Label><Input id="p" type="password" required value={password} onChange={e => setPassword(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="login-password-input" /></div>
            <Button type="submit" disabled={loading} className="w-full rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white h-12" data-testid="login-submit-button">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in'}</Button>
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => { setForgotEmail(email); setForgotSent(false); setForgotOpen(true); }}
                className="text-slate-600 hover:text-[#17352D] font-medium underline underline-offset-2"
                data-testid="login-forgot-password-link"
              >
                Forgot password?
              </button>
              <span className="text-slate-600">New here? <Link to="/signup" className="text-[#17352D] font-semibold underline">Create an account</Link></span>
            </div>
          </form>
        ) : (
          <form onSubmit={submitTotp} className="rounded-2xl border border-[#E6E9EE] bg-white p-6 space-y-4" data-testid="login-2fa-form">
            <div className="flex items-center gap-2 text-[#C8922F] mb-1">
              <ShieldCheck className="h-5 w-5" />
              <span className="text-xs uppercase tracking-[0.22em] font-semibold">Two-factor required</span>
            </div>
            <p className="text-sm text-slate-600">Open your authenticator app (Google Authenticator, Authy, 1Password) and enter the 6-digit code for <strong>Royal Palm Services</strong>.</p>

            {!useBackup ? (
              <div>
                <Label htmlFor="totp">Authentication code</Label>
                <Input
                  id="totp"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  maxLength={7}
                  required
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  className="mt-1 h-14 rounded-xl text-center font-mono text-2xl tracking-[0.4em]"
                  data-testid="login-totp-input"
                  autoFocus
                />
              </div>
            ) : (
              <div>
                <Label htmlFor="backup">Backup code</Label>
                <Input
                  id="backup"
                  required
                  value={backupCode}
                  onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                  placeholder="A1B2C3D4"
                  className="mt-1 h-11 rounded-xl font-mono uppercase tracking-widest"
                  data-testid="login-backup-input"
                  autoFocus
                />
                <p className="text-[11px] text-slate-500 mt-1">Each backup code works once. It will be consumed after this login.</p>
              </div>
            )}

            <Button type="submit" disabled={loading} className="w-full rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white h-12" data-testid="login-2fa-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify & Sign in'}
            </Button>

            <div className="flex items-center justify-between text-xs">
              <button type="button" onClick={() => { setTwoFa(false); setTotpCode(''); setBackupCode(''); setUseBackup(false); }} className="text-slate-600 hover:text-[#17352D] flex items-center gap-1" data-testid="login-2fa-back">
                <ArrowLeft className="h-3 w-3" /> Back
              </button>
              <button type="button" onClick={() => setUseBackup(v => !v)} className="text-[#17352D] font-semibold hover:text-[#C8922F]" data-testid="login-2fa-toggle-backup">
                {useBackup ? 'Use authenticator code' : 'Use a backup code'}
              </button>
            </div>
          </form>
        )}

        {/* Forgot password dialog */}
        <Dialog open={forgotOpen} onOpenChange={(o) => { setForgotOpen(o); if (!o) setForgotSent(false); }}>
          <DialogContent className="rounded-2xl" data-testid="forgot-password-dialog">
            <DialogHeader>
              <DialogTitle>Reset your password</DialogTitle>
              <DialogDescription>
                Enter your account email and we'll send you a secure reset link. The link expires in 30 minutes.
              </DialogDescription>
            </DialogHeader>
            {forgotSent ? (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3" data-testid="forgot-password-sent">
                <MailCheck className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                <div className="text-sm text-emerald-800">
                  <div className="font-semibold">Check your inbox</div>
                  <p className="mt-1 text-emerald-700">If an account exists for <strong>{forgotEmail}</strong>, a reset link is on its way. Don't forget to check spam.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={submitForgot} className="space-y-3" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="fe">Email</Label>
                  <Input id="fe" type="email" required value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} className="mt-1 h-11 rounded-xl" placeholder="you@company.com" data-testid="forgot-password-email-input" autoFocus />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={forgotBusy} className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white" data-testid="forgot-password-submit">
                    {forgotBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send reset link'}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </section>
    </Layout>
  );
}
